import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import * as Yup from 'yup';
import { Formik } from 'formik';

import AnimateButton from 'ui-component/extended/AnimateButton';
import AuthCardWrapper from './AuthCardWrapper';
import AuthWrapper2 from './AuthWrapper2';
import Logo from 'ui-component/Logo';
import useAuth from 'hooks/useAuth';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import service from 'utils/service';
import PincodeAutocomplete from 'views/shared/PincodeAutocomplete';
import type { PincodeRow } from 'views/orders/create-order/components/NewPickupAddressDialog';
import { fetchRegistrationPincodes } from './jwt/registrationApi';
import {
  ADDRESS_PROFILE_SKIP_MESSAGE,
  clearAddressProfileRequired,
  consumeAddressProfileSkipToast,
  fetchCustomerProfile,
  getLoginCustomerId,
  isAddressProfileComplete,
  markAddressProfileShellReady,
  type CustomerProfileRecord
} from 'utils/address/addressProfile';

export default function CompleteAddressPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [pincodeList, setPincodeList] = useState<PincodeRow[]>([]);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfileRecord | null>(null);
  const [initialValues, setInitialValues] = useState({
    address: '',
    pincodeId: null as number | null,
    cityName: '',
    stateName: ''
  });

  const customerId = getLoginCustomerId();

  useEffect(() => {
    if (consumeAddressProfileSkipToast()) {
      dispatch(
        openSnackbar({
          open: true,
          message: ADDRESS_PROFILE_SKIP_MESSAGE,
          variant: 'alert',
          alert: { color: 'warning' },
          close: false
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!customerId) {
        dispatch(
          openSnackbar({
            open: true,
            message: 'Session expired. Please sign in again.',
            variant: 'alert',
            alert: { color: 'error' },
            close: false
          })
        );
        navigate('/signin', { replace: true });
        return;
      }

      try {
        const [pincodeRows, customer] = await Promise.all([fetchRegistrationPincodes(), fetchCustomerProfile(customerId)]);

        if (cancelled) return;

        setPincodeList(pincodeRows);

        if (isAddressProfileComplete(customer)) {
          clearAddressProfileRequired();
          markAddressProfileShellReady();
          const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
          navigate(from && from !== '/complete-address' ? from : '/dashboard/default', { replace: true });
          return;
        }

        setCustomerProfile(customer);

        if (customer?.PINCODE_ID) {
          const matched = pincodeRows.find((item) => String(item.ID) === String(customer.PINCODE_ID));
          setInitialValues({
            address: customer.ADDRESS || '',
            pincodeId: Number(customer.PINCODE_ID) || null,
            cityName: matched?.CITY_NAME || customer.CITY || '',
            stateName: matched?.STATE_NAME || ''
          });
        } else if (customer?.ADDRESS) {
          setInitialValues({
            address: customer.ADDRESS,
            pincodeId: null,
            cityName: '',
            stateName: ''
          });
        }
      } catch {
        if (!cancelled) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Unable to load address details. Please try again.',
              variant: 'alert',
              alert: { color: 'error' },
              close: false
            })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadData();
    return () => {
      cancelled = true;
    };
  }, [customerId, dispatch, location.state, navigate]);

  const handleLogout = () => {
    logout?.();
  };

  return (
    <AuthWrapper2>
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Grid size={{ xs: 12, sm: 10, md: 6, lg: 5 }}>
          <Stack sx={{ alignItems: 'center', mb: 2 }}>
            <Box component={Link} to="#" aria-label="logo">
              <Logo />
            </Box>
          </Stack>
          <AuthCardWrapper>
            <Stack spacing={2}>
              <Typography variant="h3" sx={{ textAlign: 'center' }}>
                Complete your address
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                We couldn&apos;t find your address on file - please add your business address and pincode to continue.
              </Typography>

              {loading ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={Yup.object().shape({
                    address: Yup.string().trim().required('Business address is required').max(500, 'Address is too long'),
                    pincodeId: Yup.number().required('Pincode is required').nullable()
                  })}
                  onSubmit={async (values, { setSubmitting }) => {
                    if (!customerId) return;

                    const matchedPincode = pincodeList.find((item) => item.ID === values.pincodeId);
                    const address = values.address.trim();

                    const updateBody = {
                      ID: customerId,
                      ADDRESS: address,
                      PINCODE_ID: values.pincodeId,
                      CITY: matchedPincode?.CITY_NAME || customerProfile?.CITY || '',
                      STATE_ID: matchedPincode?.STATE_ID || customerProfile?.STATE_ID || '',
                      STATUS: customerProfile?.STATUS ?? 1
                    };

                    try {
                      const response: any = await service.makeAPICall({
                        methodName: service.Methods.PUT,
                        apiUrl: service.API_URL.user.update,
                        body: updateBody
                      });

                      if (response?.code !== 200) {
                        dispatch(
                          openSnackbar({
                            open: true,
                            message: typeof response?.message === 'string' ? response.message : 'Failed to save address. Please try again.',
                            variant: 'alert',
                            alert: { color: 'error' },
                            close: false
                          })
                        );
                        return;
                      }

                      clearAddressProfileRequired();
                      markAddressProfileShellReady();
                      dispatch(
                        openSnackbar({
                          open: true,
                          message: "Thank you! Your address has been saved. You're all set to continue.",
                          variant: 'alert',
                          alert: { color: 'success' },
                          close: false
                        })
                      );
                      navigate('/dashboard/default', { replace: true });
                    } catch {
                      dispatch(
                        openSnackbar({
                          open: true,
                          message: 'Failed to save address. Please try again.',
                          variant: 'alert',
                          alert: { color: 'error' },
                          close: false
                        })
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ values, errors, touched, handleBlur, handleChange, setFieldValue, isSubmitting, handleSubmit }) => {
                    const selectedPincode = pincodeList.find((item) => item.ID === values.pincodeId) || null;

                    return (
                      <form noValidate onSubmit={handleSubmit}>
                        <Stack spacing={2}>
                          <TextField
                            fullWidth
                            required
                            label="Business address"
                            name="address"
                            value={values.address}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.address && errors.address)}
                            helperText={touched.address && errors.address}
                          />

                          <PincodeAutocomplete
                            pincodeList={pincodeList}
                            value={selectedPincode}
                            onChange={(row) => {
                              void setFieldValue('pincodeId', row?.ID ?? null);
                              void setFieldValue('cityName', row?.CITY_NAME || '');
                              void setFieldValue('stateName', row?.STATE_NAME || '');
                            }}
                            error={Boolean(touched.pincodeId && errors.pincodeId)}
                            helperText={touched.pincodeId && errors.pincodeId ? String(errors.pincodeId) : undefined}
                            onBlur={() => handleBlur({ target: { name: 'pincodeId' } })}
                            sx={{ minWidth: '100%' }}
                          />

                          <Grid container spacing={2}>
                            <Grid size={6}>
                              <TextField fullWidth label="City" name="cityName" value={values.cityName} disabled />
                            </Grid>
                            <Grid size={6}>
                              <TextField fullWidth label="State" name="stateName" value={values.stateName} disabled />
                            </Grid>
                          </Grid>

                          <AnimateButton>
                            <Button fullWidth size="large" type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
                              {isSubmitting ? 'Saving…' : 'Save & Continue'}
                            </Button>
                          </AnimateButton>

                          <Button fullWidth variant="text" color="primary" onClick={handleLogout} disabled={isSubmitting}>
                            Logout
                          </Button>
                        </Stack>
                      </form>
                    );
                  }}
                </Formik>
              )}
            </Stack>
          </AuthCardWrapper>
        </Grid>
      </Grid>
    </AuthWrapper2>
  );
}
